import React, { useState } from 'react';
import { X, XCircle, Store, AlertCircle, Edit3, FileText, AlertTriangle } from 'lucide-react';

const REJECTION_REASONS = [
  { id: 'policy_violation', label: 'Policy Violation', defaultMessage: 'Your product violates our policies.' },
  { id: 'incomplete_info', label: 'Incomplete Information', defaultMessage: 'Please provide complete product details.' },
  { id: 'quality_issues', label: 'Quality Issues', defaultMessage: 'Product quality does not meet our standards.' },
  { id: 'copyright_infringement', label: 'Copyright Infringement', defaultMessage: 'This product infringes on intellectual property.' },
  { id: 'misleading_info', label: 'Misleading Information', defaultMessage: 'Please correct misleading product claims.' },
  { id: 'spelling_grammar', label: 'Spelling or Grammar Issues', defaultMessage: 'Please correct spelling and grammar errors.' },
  { id: 'pricing_error', label: 'Pricing Error', defaultMessage: 'Please verify and correct the pricing.' },
  { id: 'image_quality', label: 'Image Quality Issues', defaultMessage: 'Please upload higher quality images.' },
  { id: 'duplicate_product', label: 'Duplicate Product', defaultMessage: 'This product already exists in our catalog.' },
  { id: 'other', label: 'Other', defaultMessage: '' },
];

const RejectModal = ({
  isOpen,
  onClose,
  onConfirm,
  productTitle,
  vendorName,
  hasPreviousVersion,
  isProcessing = false
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [customNote, setCustomNote] = useState('');
  const [actionType, setActionType] = useState('');
  const [showManualOverride, setShowManualOverride] = useState(false);

  if (!isOpen) return null;

  const selectedReasonData = REJECTION_REASONS.find(r => r.id === selectedReason);
  const isOther = selectedReason === 'other';

  // Auto-set action type based on selected reason
  const handleReasonSelect = (reasonId: string) => {
    setSelectedReason(reasonId);
    const reason = REJECTION_REASONS.find(r => r.id === reasonId);
    if (reason) {
      // Default action mapping
      const actionMap = {
        'policy_violation': hasPreviousVersion ? 'reject_changes' : 'reject',
        'copyright_infringement': hasPreviousVersion ? 'reject_changes' : 'reject',
        'duplicate_product': hasPreviousVersion ? 'reject_changes' : 'reject',
        'spelling_grammar': 'request_changes',
        'incomplete_info': 'request_changes',
        'quality_issues': 'request_changes',
        'misleading_info': 'request_changes',
        'pricing_error': 'request_changes',
        'image_quality': 'request_changes',
        'other': hasPreviousVersion ? 'request_changes' : 'reject',
      };
      setActionType(actionMap[reasonId] || (hasPreviousVersion ? 'request_changes' : 'reject'));
    }
    setCustomNote('');
  };

  const handleConfirm = () => {
    if (!selectedReason || !actionType) return;

    let reason = '';
    if (isOther) {
      reason = customNote || 'No specific reason provided';
    } else {
      reason = selectedReasonData?.defaultMessage || '';
      if (customNote) {
        reason += `\n${customNote}`;
      }
    }

    onConfirm(actionType, reason);
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'request_changes': return 'Request Changes';
      case 'reject_changes': return 'Reject Changes';
      case 'reject': return 'Reject Product';
      default: return '';
    }
  };

  const getActionDescription = (action: string) => {
    switch (action) {
      case 'request_changes': return 'Product stays offline, vendor makes fixes';
      case 'reject_changes': return 'Product goes live with old version, changes discarded';
      case 'reject': return 'Permanently reject this draft product';
      default: return '';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'request_changes': return 'border-amber-500 bg-amber-50 text-amber-700';
      case 'reject_changes': return 'border-red-500 bg-red-50 text-red-700';
      case 'reject': return 'border-red-600 bg-red-100 text-red-800';
      default: return '';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'request_changes': return <Edit3 className="h-4 w-4" />;
      case 'reject_changes': return <AlertCircle className="h-4 w-4" />;
      case 'reject': return <XCircle className="h-4 w-4" />;
      default: return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${actionType === 'request_changes' ? 'bg-amber-100' : 'bg-red-100'
              }`}>
              <XCircle className={`h-5 w-5 ${actionType === 'request_changes' ? 'text-amber-600' : 'text-red-600'
                }`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {actionType ? getActionLabel(actionType) : 'Review Decision'}
              </h3>
              <p className="text-sm text-gray-500">{productTitle}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Vendor Info */}
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
            <Store className="h-4 w-4 text-gray-400" />
            <span>Vendor: <span className="font-medium text-gray-900">{vendorName}</span></span>
          </div>

          {/* Manual Action Override */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Decision Type <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowManualOverride(!showManualOverride)}
                className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${showManualOverride
                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                {showManualOverride ? '✓ Auto mode' : 'Manual override'}
              </button>
            </div>

            <div className="space-y-2">
              {/* Auto-selected action (read-only display) */}
              {!showManualOverride && actionType && (
                <div className={`p-3 rounded-lg border ${getActionColor(actionType)}`}>
                  <div className="flex items-center gap-2">
                    {getActionIcon(actionType)}
                    <span className="text-sm font-medium">{getActionLabel(actionType)}</span>
                  </div>
                  <p className="text-xs opacity-75 mt-0.5">{getActionDescription(actionType)}</p>
                </div>
              )}

              {/* Manual override options */}
              {showManualOverride && (
                <div className="space-y-2">
                  {/* Request Changes */}
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${actionType === 'request_changes'
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-gray-200 hover:bg-gray-50'
                    }`}>
                    <input
                      type="radio"
                      name="actionType"
                      value="request_changes"
                      checked={actionType === 'request_changes'}
                      onChange={() => setActionType('request_changes')}
                      className="mt-0.5 flex-shrink-0 h-4 w-4 text-amber-600"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Request Changes</p>
                      <p className="text-xs text-gray-500">Product stays offline, vendor makes fixes</p>
                    </div>
                  </label>

                  {/* Reject Changes (only if has previous version) */}
                  {hasPreviousVersion && (
                    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${actionType === 'reject_changes'
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                      <input
                        type="radio"
                        name="actionType"
                        value="reject_changes"
                        checked={actionType === 'reject_changes'}
                        onChange={() => setActionType('reject_changes')}
                        className="mt-0.5 flex-shrink-0 h-4 w-4 text-red-600"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Reject Changes</p>
                        <p className="text-xs text-gray-500">Product goes live with old version, changes discarded</p>
                      </div>
                    </label>
                  )}

                  {/* Reject Product (draft only) */}
                  {!hasPreviousVersion && (
                    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${actionType === 'reject'
                      ? 'border-red-600 bg-red-100'
                      : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                      <input
                        type="radio"
                        name="actionType"
                        value="reject"
                        checked={actionType === 'reject'}
                        onChange={() => setActionType('reject')}
                        className="mt-0.5 flex-shrink-0 h-4 w-4 text-red-700"
                      />
                      <div>
                        <p className="text-sm font-medium text-red-700">Reject Product</p>
                        <p className="text-xs text-gray-500">Permanently reject this draft product</p>
                      </div>
                    </label>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Rejection Reasons */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {REJECTION_REASONS.map((reason) => {
                // Hide 'reject' options for products with previous version
                if (reason.id === 'policy_violation' || reason.id === 'copyright_infringement' || reason.id === 'duplicate_product') {
                  if (hasPreviousVersion && !showManualOverride) return null;
                }
                return (
                  <label
                    key={reason.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedReason === reason.id
                      ? actionType === 'request_changes'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:bg-gray-50'
                      }`}
                  >
                    <input
                      type="radio"
                      name="rejectionReason"
                      value={reason.id}
                      checked={selectedReason === reason.id}
                      onChange={(e) => handleReasonSelect(e.target.value)}
                      className="mt-0.5 flex-shrink-0 h-4 w-4 text-red-600"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{reason.label}</p>
                      {reason.defaultMessage && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {reason.defaultMessage}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Custom Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note {isOther && <span className="text-red-500">*</span>}
              <span className="text-xs text-gray-500 ml-1">(optional)</span>
            </label>
            <textarea
              value={customNote}
              onChange={(e) => setCustomNote(e.target.value)}
              placeholder={isOther ? "Please specify the reason..." : "Add additional context (optional)"}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Preview */}
          {selectedReason && selectedReason !== 'other' && selectedReasonData?.defaultMessage && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                Vendor Will See:
              </p>
              <p className="text-sm text-gray-700">
                {selectedReasonData.defaultMessage}
                {customNote && `\n${customNote}`}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isProcessing || !selectedReason || !actionType}
            className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${actionType === 'request_changes'
              ? 'bg-amber-600 hover:bg-amber-700'
              : 'bg-red-600 hover:bg-red-700'
              }`}
          >
            {isProcessing ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Processing...
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4" />
                {actionType ? getActionLabel(actionType) : 'Submit'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectModal;